import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Container,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Button,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { PATH_DASHBOARD } from '../../../routes/paths';
import CampaignStep1Basic from './components/CampaignStep1Basic';
import CampaignStep2Budget from './components/CampaignStep2Budget';
import CampaignStep3Summary from './components/CampaignStep3Summary';
import { useCreateCampaign } from './hooks/useCampaigns';
import { campaignApi } from '../../../api/campaign';
import { translateKey } from 'src/locales/translate';


const STEPS = [
  'Informacje podstawowe',
  translateKey("hardcoded.budzet_i_harmonogram"),
  translateKey("hardcoded.podsumowanie_i_platnosc"),
];

export interface CampaignFormValues {
  name: string;
  description: string;
  keywords: string[];
  dailyBudget: number;
  startDate: Date | null;
  endDate: Date | null;
}

const DEFAULT_FORM_VALUES: CampaignFormValues = {
  name: '',
  description: '',
  keywords: [],
  dailyBudget: 50,
  startDate: null,
  endDate: null,
};

export default function CreateCampaignWizard() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [activeStep, setActiveStep] = useState(0);
  const [formValues, setFormValues] =
    useState<CampaignFormValues>(DEFAULT_FORM_VALUES);
  const [isCreating, setIsCreating] = useState(false);

  const createCampaignMutation = useCreateCampaign(organizationId || '');

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleStep1Complete = (values: {
    name: string;
    description: string;
    keywords: string[];
  }) => {
    setFormValues((prev) => ({ ...prev, ...values }));
    setActiveStep(1);
  };

  const handleStep2Complete = (values: {
    dailyBudget: number;
    startDate: Date;
    endDate: Date;
  }) => {
    setFormValues((prev) => ({ ...prev, ...values }));
    setActiveStep(2);
  };

  const handlePayment = async (promoCode?: string) => {
    if (!organizationId) return;

    setIsCreating(true);
    try {
      // 1. Create the campaign
      const campaign = await createCampaignMutation.mutateAsync({
        name: formValues.name,
        description: formValues.description,
        keywords: formValues.keywords,
        dailyBudget: formValues.dailyBudget,
        startDate: formValues.startDate?.toISOString().split('T')[0],
        endDate: formValues.endDate?.toISOString().split('T')[0],
        promoCode: promoCode || undefined,
      });

      // 2. Initiate payment for the campaign
      const paymentResponse =
        await campaignApi.campaignControllerInitiatePayment({
          campaignId: campaign.id,
        });

      // 3. Redirect to payment URL or navigate directly if free (100% discount)
      if (paymentResponse.data.paymentUrl) {
        enqueueSnackbar(translateKey("hardcoded.przekierowanie_do_platnosci"), { variant: 'info' });
        window.location.replace(paymentResponse.data.paymentUrl);
      } else {
        enqueueSnackbar(translateKey("hardcoded.kampania_zostala_aktywowana"), { variant: 'success' });
        navigate(
          PATH_DASHBOARD.general.organizations.campaigns(organizationId),
        );
      }
    } catch (error: any) {
      console.error('Failed to create campaign:', error);
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_tworzenia_kampanii"), {
        variant: 'error',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    if (organizationId) {
      navigate(PATH_DASHBOARD.general.organizations.campaigns(organizationId));
    }
  };

  if (!organizationId) {
    return (
      <Container maxWidth="md">
        <Typography>Brak identyfikatora organizacji</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>{translateKey("hardcoded.utworz_nowa_kampanie")}</Typography>
        <Typography color="text.secondary">{translateKey("hardcoded.skonfiguruj_kampanie_google_ads_dla_swojej_organizacji")}</Typography>
      </Box>

      <Card sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <CampaignStep1Basic
            initialValues={{
              name: formValues.name,
              description: formValues.description,
              keywords: formValues.keywords,
            }}
            organizationId={organizationId}
            onNext={handleStep1Complete}
            onCancel={handleCancel}
            isLoading={createCampaignMutation.isPending}
          />
        )}

        {activeStep === 1 && (
          <CampaignStep2Budget
            initialValues={{
              dailyBudget: formValues.dailyBudget,
              startDate: formValues.startDate,
              endDate: formValues.endDate,
            }}
            onNext={handleStep2Complete}
            onBack={handleBack}
            isLoading={false}
          />
        )}

        {activeStep === 2 && (
          <CampaignStep3Summary
            formValues={formValues}
            onPayment={handlePayment}
            onBack={handleBack}
            isLoading={isCreating}
          />
        )}
      </Card>
    </Container>
  );
}
